import * as Sentry from '@sentry/react'
import { useMutation } from '@apollo/client'
import { userLessonUpsertOne } from '../../graphql/UserLessons/userLessonUpsertOne'
import { LessonType, CourseType } from '../../types'
import { getCourseLessons } from '../CourseLibraryView/utils/getCourseLessons'

export const useLesson = () => {
  const [UserLessonUpsertOne] = useMutation(userLessonUpsertOne)

  // Returns the next lesson after the provided lesson in the course
  const getNextLesson = async (lesson: LessonType, course: CourseType) => {
    const courseLessons = getCourseLessons(course)
    const lessonIndex = courseLessons.indexOf(lesson)

    if (lessonIndex === courseLessons.length - 1) {
      return
    }

    return courseLessons[lessonIndex + 1]
  }

  // Marks the designated lesson as complete
  const handleComplete = async (lesson: LessonType) => {
    try {
      return await UserLessonUpsertOne({
        variables: {
          lesson: lesson._id,
          course: lesson.course,
          isCompleted: lesson.isCompleted,
          isSkipped: false
        }
      })
    }
    catch (err) {
      Sentry.captureException(err)
    }
  }

  // If the designated lesson is not completed and not skipped,
  // it marks it as skipped
  const handleSkip = async (lesson: LessonType) => {
    try {
      // If the lesson is already skipped, or is completed
      // don't bother making the GraphQL call
      if (lesson.isSkipped || lesson.isCompleted) {
        return
      }

      return await UserLessonUpsertOne({
        variables: {
          lesson: lesson._id,
          course: lesson.course,
          isSkipped: true
        }
      })
    }
    catch (err) {
      Sentry.captureException(err)
    }
  }

  // Unskips the designated lesson
  const handleUnSkip = async (lesson: LessonType) => {
    try {
      if (!lesson.isSkipped) {
        return
      }

      return await UserLessonUpsertOne({
        variables: {
          lesson: lesson._id,
          course: lesson.course,
          isSkipped: false
        }
      })
    }
    catch (err) {
      Sentry.captureException(err)
    }
  }

  return {
    getNextLesson,
    handleComplete,
    handleSkip,
    handleUnSkip
  }
}
